// src/components/BonusPuzzleManagement.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { firestore } from '../firebase';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { normalizePuzzleUrl } from '../utils/urlUtils';
import { openPrintWindow, getOperationalTablePrintCss } from '../utils/printUtils';
import { copyText, buildTsvTable } from '../utils/clipboardUtils';
import AdminCopyMenu from './admin/AdminCopyMenu';
import AdminColumnExportModal from './admin/AdminColumnExportModal';
import { collectInvalidNamedControlNames } from '../utils/collectInvalidNamedControlNames';
import ModalFieldRequiredError from './ModalFieldRequiredError';
import './BonusPuzzleManagement.css';
import './admin/FmModal.css';
import { useModalEscapeKey } from '../utils/useModalEscapeKey';
import JumpItOrFixItStationsAdminEditor, { normalizeJumpItCustomLocation } from './JumpItOrFixItStationsAdminEditor';
import { fetchSponsorsMergedForEvent } from '../utils/sponsorQueries';

function escapeHtmlCell(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const PUZZLE_TABLE_EXPORT_COLS = [
  { id: 'number', label: '#', default: true },
  { id: 'internalName', label: 'Internal name', default: false },
  { id: 'format', label: 'Format', default: false },
  { id: 'title', label: 'Title', default: true },
  { id: 'value', label: 'Value', default: true },
  { id: 'tagline', label: 'Tagline', default: false },
  { id: 'type', label: 'Type', default: true },
  { id: 'location', label: 'Location', default: true },
  { id: 'answer', label: 'Answer', default: false },
  { id: 'publicCode', label: 'Public code', default: true },
  { id: 'puzzleUrl', label: 'URL', default: true },
  { id: 'onDashboard', label: 'On dashboard', default: false },
  { id: 'dashboardUrl', label: 'Dashboard URL', default: false },
  { id: 'enabled', label: 'Status', default: true },
];

// Quiz Questions Editor Component
const QuizQuestionsEditor = ({ questions, onChange }) => {
  const addQuestion = () => {
    onChange([...questions, {
      question: '',
      type: 'short-answer',
      options: [],
      correctAnswer: '',
      correctAnswers: [] // For multiple choice multiple
    }]);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const deleteQuestion = (index) => {
    onChange(questions.filter((_, i) => i !== index));
  };

  const addOption = (questionIndex) => {
    const updated = [...questions];
    if (!updated[questionIndex].options) {
      updated[questionIndex].options = [];
    }
    updated[questionIndex].options.push('');
    onChange(updated);
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const updated = [...questions];
    updated[questionIndex].options[optionIndex] = value;
    onChange(updated);
  };

  const deleteOption = (questionIndex, optionIndex) => {
    const updated = [...questions];
    updated[questionIndex].options = updated[questionIndex].options.filter((_, i) => i !== optionIndex);
    onChange(updated);
  };

  const toggleCorrectAnswer = (questionIndex, optionIndex) => {
    const updated = [...questions];
    const question = updated[questionIndex];
    if (!question.correctAnswers) {
      question.correctAnswers = [];
    }
    const idx = question.correctAnswers.indexOf(optionIndex);
    if (idx > -1) {
      question.correctAnswers.splice(idx, 1);
    } else {
      question.correctAnswers.push(optionIndex);
    }
    onChange(updated);
  };

  return (
    <div className="quiz-questions-editor">
      <h4>Quiz Questions</h4>
      {questions.map((q, qIndex) => (
        <div key={qIndex} className="question-editor-item">
          <div className="form-group">
            <label>Question {qIndex + 1}:</label>
            <textarea
              value={q.question}
              onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
              placeholder="Enter question text"
              rows="2"
            />
          </div>
          <div className="form-group">
            <label>Question Type:</label>
            <select
              value={q.type}
              onChange={(e) => {
                const newType = e.target.value;
                const updated = { ...q, type: newType };
                if (newType === 'short-answer') {
                  updated.options = [];
                  updated.correctAnswers = [];
                } else if (newType === 'multiple-choice-single') {
                  updated.correctAnswer = '';
                  updated.correctAnswers = [];
                } else if (newType === 'multiple-choice-multiple') {
                  updated.correctAnswer = '';
                  if (!updated.correctAnswers) updated.correctAnswers = [];
                }
                updateQuestion(qIndex, 'type', newType);
                onChange(questions.map((item, i) => i === qIndex ? updated : item));
              }}
            >
              <option value="short-answer">Short Answer</option>
              <option value="multiple-choice-single">Multiple Choice (Single)</option>
              <option value="multiple-choice-multiple">Multiple Choice (Multiple)</option>
            </select>
          </div>

          {q.type === 'short-answer' && (
            <div className="form-group">
              <label>Correct Answer:</label>
              <input
                type="text"
                value={q.correctAnswer || ''}
                onChange={(e) => updateQuestion(qIndex, 'correctAnswer', e.target.value)}
                placeholder="Enter correct answer (case-insensitive)"
              />
            </div>
          )}

          {(q.type === 'multiple-choice-single' || q.type === 'multiple-choice-multiple') && (
            <div className="form-group">
              <label>Options:</label>
              {(q.options || []).map((option, oIndex) => (
                <div key={oIndex} className="option-row">
                  <input
                    type={q.type === 'multiple-choice-multiple' ? 'checkbox' : 'radio'}
                    checked={
                      q.type === 'multiple-choice-single'
                        ? q.correctAnswer === oIndex.toString()
                        : (q.correctAnswers || []).includes(oIndex)
                    }
                    onChange={() => {
                      if (q.type === 'multiple-choice-single') {
                        updateQuestion(qIndex, 'correctAnswer', oIndex.toString());
                      } else {
                        toggleCorrectAnswer(qIndex, oIndex);
                      }
                    }}
                  />
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                    placeholder={`Option ${oIndex + 1}`}
                    style={{ flex: 1, marginLeft: '8px', marginRight: '8px' }}
                  />
                  <button
                    type="button"
                    onClick={() => deleteOption(qIndex, oIndex)}
                    style={{ padding: '4px 8px', fontSize: '12px' }}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addOption(qIndex)}
                style={{ marginTop: '8px', padding: '4px 8px', fontSize: '12px' }}
              >
                + Add Option
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={() => deleteQuestion(qIndex)}
            style={{ marginTop: '8px', padding: '4px 8px', fontSize: '12px', backgroundColor: '#dc3545' }}
          >
            Delete Question
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addQuestion}
        style={{ marginTop: '16px', padding: '8px 16px' }}
      >
        + Add Question
      </button>
    </div>
  );
};

// Hard-coded public codes for puzzles 1-40 (example values)
const DEFAULT_PUBLIC_CODES = {
  "1": "9a8b7c",
  "2": "d4e3f2",
  "3": "x1y2z3",
  "4": "a1b2c3",
  "5": "m5n6o7",
  "6": "p8q9r0",
  "7": "z1x2c3",
  "8": "v4b5n6",
  "9": "l7k8j9",
  "10": "q1w2e3",
  // ... add codes for puzzles 11 to 40 as needed ...
};

const BONUS_PUZZLE_TYPES = ['simple', 'code verified', 'custom', 'quiz', 'videoSubmission', 'jumpItOrFixIt'];
const CATALOG_COLLECTION = 'bonusPuzzleCatalog';

/** Suggested format labels (free text allowed). */
const BONUS_PUZZLE_FORMAT_PRESETS = [
  'Print',
  'Digital / URL',
  'Physical',
  'QR-led',
  'In-person / host',
  'Hybrid',
  'Other',
];

function getEventEditionYear(ev) {
  const raw = ev?.editionYear;
  const y = Number.parseInt(String(raw ?? '').trim(), 10);
  if (Number.isInteger(y) && y >= 2000 && y <= 9999) return y;
  const dateStr = (ev?.date || '').toString().trim();
  const m = dateStr.match(/^(\d{4})-\d{2}-\d{2}$/);
  if (m) {
    const y2 = Number.parseInt(m[1], 10);
    if (Number.isInteger(y2)) return y2;
  }
  return null;
}

function createEmptyPuzzleForm() {
  return {
    number: '',
    internalName: '',
    format: '',
    title: '',
    value: '',
    tagline: '',
    type: 'simple',
    location: '',
    answer: '',
    alternativeAnswers: [],
    publicCode: '',
    onDashboard: false,
    dashboardUrl: '',
    questions: [],
    basePoints: '',
    creativityMaxPoints: '',
    jumpItStations: [],
    jumpItCustomSponsorLocations: [],
  };
}

function toCatalogKey(puzzle) {
  const title = (puzzle.title || '').toString().trim().toLowerCase();
  const type = (puzzle.type || '').toString().trim().toLowerCase();
  return `${title}::${type}`;
}

function makeCodeCandidate() {
  return Math.random().toString(36).slice(2, 8);
}

const BonusPuzzleManagement = () => {
  // State for active events (dropdown)
  const [activeEvents, setActiveEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');

  // State for bonus puzzles
  const [puzzles, setPuzzles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Modal state for add/edit form
  const [modalOpen, setModalOpen] = useState(false);
  // Modal state for duplication prompt
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);

  // Helper to parse alternative answers from textarea
  const parseAlternativeAnswers = (text) => {
    if (!text) return [];
    return text
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  };

  // State for the puzzle form (add/edit)
  const [newPuzzle, setNewPuzzle] = useState(createEmptyPuzzleForm());
  // Holds the document ID of the puzzle being edited (if any)
  const [editingPuzzleId, setEditingPuzzleId] = useState(null);

  // State for duplication: the puzzle to duplicate and target event
  const [duplicatePuzzle, setDuplicatePuzzle] = useState(null);
  const [duplicateEvent, setDuplicateEvent] = useState('');

  // State for print sheet: puzzle to generate sheet for, null when modal closed
  const [printSheetPuzzle, setPrintSheetPuzzle] = useState(null);
  const [puzzleTableExport, setPuzzleTableExport] = useState(null); // null | { mode: 'print'|'copy' }
  const [puzzleExportCols, setPuzzleExportCols] = useState(() => new Set());
  const [catalogPuzzles, setCatalogPuzzles] = useState([]);
  const [catalogSelection, setCatalogSelection] = useState(() => new Set());
  const [catalogModalOpen, setCatalogModalOpen] = useState(false);
  const [bonusPuzzleFormInvalidNames, setBonusPuzzleFormInvalidNames] = useState(() => new Set());
  const [catalogPuzzleFormInvalidNames, setCatalogPuzzleFormInvalidNames] = useState(() => new Set());
  const [editingCatalogId, setEditingCatalogId] = useState(null);
  const [catalogForm, setCatalogForm] = useState(createEmptyPuzzleForm());
  const [activeTab, setActiveTab] = useState('event');
  /** Minimal rows from all events for catalog “years used” (catalogPuzzleId linkage). */
  const [allBonusPuzzleLinks, setAllBonusPuzzleLinks] = useState([]);
  const [catalogUsageLoading, setCatalogUsageLoading] = useState(false);
  const [catalogSearchQuery, setCatalogSearchQuery] = useState('');
  const [catalogFilterType, setCatalogFilterType] = useState('');
  const [catalogFilterFormat, setCatalogFilterFormat] = useState('');
  const [catalogFilterYear, setCatalogFilterYear] = useState('');
  const [catalogHideUsedInSelectedEvent, setCatalogHideUsedInSelectedEvent] = useState(false);
  const [catalogSortKey, setCatalogSortKey] = useState('title');

  const jumpItNeedsSponsors =
    !!selectedEvent &&
    ((modalOpen && newPuzzle.type === 'jumpItOrFixIt') ||
      (catalogModalOpen && catalogForm.type === 'jumpItOrFixIt'));

  const [jumpItMergedSponsors, setJumpItMergedSponsors] = useState([]);
  const [jumpItSponsorsLoading, setJumpItSponsorsLoading] = useState(false);

  useEffect(() => {
    if (!jumpItNeedsSponsors) {
      setJumpItMergedSponsors([]);
      setJumpItSponsorsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setJumpItSponsorsLoading(true);
    fetchSponsorsMergedForEvent(selectedEvent)
      .then((rows) => {
        if (!cancelled) setJumpItMergedSponsors(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setJumpItMergedSponsors([]);
      })
      .finally(() => {
        if (!cancelled) setJumpItSponsorsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jumpItNeedsSponsors, selectedEvent]);

  useEffect(() => {
    if (!modalOpen) setBonusPuzzleFormInvalidNames(new Set());
  }, [modalOpen]);

  useEffect(() => {
    if (!catalogModalOpen) setCatalogPuzzleFormInvalidNames(new Set());
  }, [catalogModalOpen]);

  // Fetch active events for the dropdown
  useEffect(() => {
    let cancelled = false;
    async function fetchEvents() {
      try {
        const [querySnapshot, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const eventsData = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setActiveEvents(eventsData);
        if (eventsData.length > 0) {
          setSelectedEvent(
            resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events: eventsData })
          );
        }
      } catch (err) {
        if (!cancelled) setError('Failed to fetch events: ' + err.message);
      }
    }
    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch bonus puzzles for the selected event
  useEffect(() => {
    async function fetchPuzzles() {
      if (!selectedEvent) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const querySnapshot = await getDocs(collection(firestore, 'bonusPuzzles'));
        const puzzlesData = querySnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(puzzle => puzzle.eventId === selectedEvent);
        setPuzzles(puzzlesData);
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch puzzles: ' + err.message);
        setLoading(false);
      }
    }
    fetchPuzzles();
  }, [selectedEvent]);

  const reloadCatalogAndUsage = useCallback(async () => {
    setCatalogUsageLoading(true);
    try {
      const [catSnap, bonusSnap] = await Promise.all([
        getDocs(collection(firestore, CATALOG_COLLECTION)),
        getDocs(collection(firestore, 'bonusPuzzles')),
      ]);
      const data = catSnap.docs
        .map((catalogDoc) => ({ id: catalogDoc.id, ...catalogDoc.data() }))
        .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      setCatalogPuzzles(data);
      const links = bonusSnap.docs.map((d) => {
        const x = d.data() || {};
        return {
          id: d.id,
          eventId: (x.eventId || '').toString(),
          catalogPuzzleId: x.catalogPuzzleId ? String(x.catalogPuzzleId) : null,
        };
      });
      setAllBonusPuzzleLinks(links);
    } catch (err) {
      setError('Failed to load catalog / usage: ' + err.message);
    } finally {
      setCatalogUsageLoading(false);
    }
  }, []);

  useEffect(() => {
    reloadCatalogAndUsage();
  }, [reloadCatalogAndUsage]);

  const sortedPuzzles = useMemo(
    () => puzzles.slice().sort((a, b) => Number(a.number) - Number(b.number)),
    [puzzles]
  );

  const eventYearById = useMemo(() => {
    const m = new Map();
    activeEvents.forEach((ev) => {
      const y = getEventEditionYear(ev);
      if (y != null) m.set(ev.id, y);
    });
    return m;
  }, [activeEvents]);

  const catalogUsageByCatalogId = useMemo(() => {
    const map = new Map();
    for (const row of allBonusPuzzleLinks) {
      if (!row.catalogPuzzleId) continue;
      if (!map.has(row.catalogPuzzleId)) {
        map.set(row.catalogPuzzleId, { years: new Set(), eventIds: new Set() });
      }
      const entry = map.get(row.catalogPuzzleId);
      entry.eventIds.add(row.eventId);
      const y = eventYearById.get(row.eventId);
      if (y != null) entry.years.add(y);
    }
    return map;
  }, [allBonusPuzzleLinks, eventYearById]);

  const catalogDistinctFormats = useMemo(() => {
    const s = new Set();
    catalogPuzzles.forEach((p) => {
      const f = (p.format || '').toString().trim();
      if (f) s.add(f);
    });
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [catalogPuzzles]);

  const catalogDistinctYears = useMemo(() => {
    const s = new Set();
    activeEvents.forEach((ev) => {
      const y = getEventEditionYear(ev);
      if (y != null) s.add(y);
    });
    catalogUsageByCatalogId.forEach((u) => {
      u.years.forEach((y) => s.add(y));
    });
    return [...s].sort((a, b) => b - a);
  }, [activeEvents, catalogUsageByCatalogId]);

  const filteredSortedCatalogPuzzles = useMemo(() => {
    let list = catalogPuzzles.slice();
    const qRaw = catalogSearchQuery.trim().toLowerCase();
    if (qRaw) {
      const tokens = qRaw.split(/\s+/).filter(Boolean);
      list = list.filter((p) => {
        const hay = [
          p.internalName,
          p.title,
          p.tagline,
          p.location,
          p.format,
          p.type,
          p.value,
          String(p.number ?? ''),
        ]
          .map((x) => String(x ?? '').toLowerCase())
          .join(' ');
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (catalogFilterType) {
      list = list.filter((p) => (p.type || '') === catalogFilterType);
    }
    if (catalogFilterFormat) {
      list = list.filter(
        (p) => String(p.format || '').toLowerCase() === catalogFilterFormat.toLowerCase()
      );
    }
    if (catalogFilterYear === 'never') {
      list = list.filter((p) => {
        const u = catalogUsageByCatalogId.get(p.id);
        return !u || u.eventIds.size === 0;
      });
    } else if (catalogFilterYear) {
      const y = Number.parseInt(catalogFilterYear, 10);
      if (Number.isInteger(y)) {
        list = list.filter((p) => {
          const u = catalogUsageByCatalogId.get(p.id);
          return u && u.years.has(y);
        });
      }
    }
    if (catalogHideUsedInSelectedEvent && selectedEvent) {
      list = list.filter((p) => {
        const u = catalogUsageByCatalogId.get(p.id);
        return !u || !u.eventIds.has(selectedEvent);
      });
    }

    const usageCount = (id) => catalogUsageByCatalogId.get(id)?.eventIds.size ?? 0;

    list.sort((a, b) => {
      switch (catalogSortKey) {
        case 'internalName':
          return String(a.internalName || '').localeCompare(String(b.internalName || ''));
        case 'type':
          return String(a.type || '').localeCompare(String(b.type || ''));
        case 'format':
          return String(a.format || '').localeCompare(String(b.format || ''));
        case 'usesDesc':
          return usageCount(b.id) - usageCount(a.id) || String(a.title || '').localeCompare(String(b.title || ''));
        case 'usesAsc':
          return usageCount(a.id) - usageCount(b.id) || String(a.title || '').localeCompare(String(b.title || ''));
        case 'yearsDesc': {
          const ya = [...(catalogUsageByCatalogId.get(a.id)?.years || [])].sort((x, y) => y - x)[0] ?? -1;
          const yb = [...(catalogUsageByCatalogId.get(b.id)?.years || [])].sort((x, y) => y - x)[0] ?? -1;
          return yb - ya || String(a.title || '').localeCompare(String(b.title || ''));
        }
        default:
          return String(a.title || '').localeCompare(String(b.title || ''));
      }
    });
    return list;
  }, [
    catalogPuzzles,
    catalogSearchQuery,
    catalogFilterType,
    catalogFilterFormat,
    catalogFilterYear,
    catalogHideUsedInSelectedEvent,
    selectedEvent,
    catalogSortKey,
    catalogUsageByCatalogId,
  ]);

  const puzzleTableExportRows = useMemo(() => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    return sortedPuzzles.map((puzzle) => {
      const dashboardUrlTrimmed = (puzzle.dashboardUrl || '').trim();
      const url =
        puzzle.onDashboard && dashboardUrlTrimmed
          ? normalizePuzzleUrl(dashboardUrlTrimmed)
          : puzzle.publicCode
            ? `${baseUrl}/p/${puzzle.publicCode}`
            : '';
      return {
        number: String(puzzle.number ?? ''),
        internalName: puzzle.internalName || '',
        format: puzzle.format || '',
        title: puzzle.title || '',
        value: String(puzzle.value ?? ''),
        tagline: puzzle.tagline || '',
        type: puzzle.type || '',
        location: puzzle.location || '',
        answer: puzzle.answer || '',
        publicCode: puzzle.publicCode || '',
        puzzleUrl: url,
        onDashboard: puzzle.onDashboard ? 'Yes' : 'No',
        dashboardUrl: dashboardUrlTrimmed,
        enabled: puzzle.enabled ? 'Enabled' : 'Disabled',
      };
    });
  }, [sortedPuzzles]);

  useEffect(() => {
    if (!puzzleTableExport) return;
    setPuzzleExportCols(
      new Set(PUZZLE_TABLE_EXPORT_COLS.filter((c) => c.default).map((c) => c.id))
    );
  }, [puzzleTableExport]);

  const openPuzzleTableExport = (mode) => {
    if (!puzzleTableExportRows.length) {
      window.alert('No puzzles to export for this event.');
      return;
    }
    setPuzzleTableExport({ mode });
  };

  const resetPuzzleExportCols = () => {
    setPuzzleExportCols(
      new Set(PUZZLE_TABLE_EXPORT_COLS.filter((c) => c.default).map((c) => c.id))
    );
  };

  const runPuzzleTableExport = (selectedColumnIds) => {
    if (!puzzleTableExport) return;
    const { mode } = puzzleTableExport;
    const colSet =
      Array.isArray(selectedColumnIds) && selectedColumnIds.length > 0
        ? new Set(selectedColumnIds)
        : puzzleExportCols;
    const active = PUZZLE_TABLE_EXPORT_COLS.filter((c) => colSet.has(c.id));
    if (!active.length) return;
    const colMeta = active.map((c) => ({ id: c.id, header: c.label }));
    const rowRecords = puzzleTableExportRows.map((r) => {
      const o = {};
      active.forEach((c) => {
        o[c.id] = r[c.id] ?? '';
      });
      return o;
    });
    const ev = activeEvents.find((e) => e.id === selectedEvent);
    const evTitle = ev?.title || selectedEvent || 'Bonus puzzles';
    const sub = 'Bonus puzzle list';
    if (mode === 'copy') {
      copyText(buildTsvTable(colMeta, rowRecords));
    } else {
      const headerCells = active.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
      const rowsHtml = rowRecords
        .map((rec) => {
          const cells = active.map((c) => `<td>${escapeHtmlCell(rec[c.id])}</td>`).join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      const bodyHtml = `<main style="padding:8px 12px"><h1>${escapeHtmlCell(evTitle)}</h1><p>${escapeHtmlCell(sub)}</p><p>${rowRecords.length} puzzle(s)</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table></main>`;
      openPrintWindow(`${evTitle} — ${sub}`, bodyHtml, getOperationalTablePrintCss());
    }
    setPuzzleTableExport(null);
  };

  const handleActiveEventChange = (e) => {
    setSelectedEvent(e.target.value);
    setLoading(true);
  };

  const resetPuzzleForm = () => {
    setNewPuzzle(createEmptyPuzzleForm());
  };

  const resetCatalogForm = () => {
    setCatalogForm(createEmptyPuzzleForm());
  };

  const ensureUniquePublicCode = async (preferredCode = '', ignorePuzzleId = null) => {
    let candidate = (preferredCode || '').toString().trim();
    const attempts = candidate ? [candidate] : [];
    for (let i = 0; i < 20; i++) {
      attempts.push(makeCodeCandidate());
    }
    for (const next of attempts) {
      if (!next) continue;
      const snap = await getDocs(query(collection(firestore, 'bonusPuzzles'), where('publicCode', '==', next)));
      const conflict = snap.docs.find((d) => d.id !== ignorePuzzleId);
      if (!conflict) return next;
    }
    return '';
  };

  // Prepares puzzle data for saving.
  // If no publicCode is entered, use the default mapping based on puzzle number.
  const preparePuzzleData = async () => {
    let publicCode = newPuzzle.publicCode.trim();
    if (!publicCode && newPuzzle.number in DEFAULT_PUBLIC_CODES) {
      publicCode = DEFAULT_PUBLIC_CODES[newPuzzle.number];
    }
    publicCode = await ensureUniquePublicCode(publicCode, editingPuzzleId);
    const out = {
      ...newPuzzle,
      internalName: (newPuzzle.internalName || '').trim(),
      format: (newPuzzle.format || '').trim(),
      publicCode,
      eventId: selectedEvent,
    };
    if (!out.catalogPuzzleId) delete out.catalogPuzzleId;
    return out;
  };

  // Handle add/edit puzzle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      setBonusPuzzleFormInvalidNames(collectInvalidNamedControlNames(form));
      const inv = form.querySelector(':invalid');
      if (inv && typeof inv.focus === 'function') inv.focus();
      return;
    }
    setBonusPuzzleFormInvalidNames(new Set());
    try {
      const puzzleData = await preparePuzzleData();

      if (editingPuzzleId) {
        // Update existing puzzle
        const puzzleDocRef = doc(firestore, 'bonusPuzzles', editingPuzzleId);
        await updateDoc(puzzleDocRef, puzzleData);
        setPuzzles(
          puzzles.map(p =>
            p.id === editingPuzzleId ? { id: editingPuzzleId, ...puzzleData } : p
          )
        );
        setAllBonusPuzzleLinks((prev) =>
          prev.map((r) =>
            r.id === editingPuzzleId
              ? {
                  ...r,
                  eventId: selectedEvent,
                  catalogPuzzleId: puzzleData.catalogPuzzleId ? String(puzzleData.catalogPuzzleId) : null,
                }
              : r
          )
        );
        setEditingPuzzleId(null);
      } else {
        // Create a new puzzle; new puzzles default to enabled
        const docRef = await addDoc(collection(firestore, 'bonusPuzzles'), {
          ...puzzleData,
          enabled: true,
        });
        setPuzzles([...puzzles, { id: docRef.id, ...puzzleData, enabled: true }]);
        setAllBonusPuzzleLinks((prev) => [
          ...prev,
          {
            id: docRef.id,
            eventId: selectedEvent,
            catalogPuzzleId: puzzleData.catalogPuzzleId ? String(puzzleData.catalogPuzzleId) : null,
          },
        ]);
      }
      // Reset form and close modal
      resetPuzzleForm();
      setModalOpen(false);
    } catch (err) {
      setError('Error saving puzzle: ' + err.message);
    }
  };

  const saveCatalogPuzzle = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      setCatalogPuzzleFormInvalidNames(collectInvalidNamedControlNames(form));
      const inv = form.querySelector(':invalid');
      if (inv && typeof inv.focus === 'function') inv.focus();
      return;
    }
    setCatalogPuzzleFormInvalidNames(new Set());
    try {
      const payload = {
        ...catalogForm,
        internalName: (catalogForm.internalName || '').trim(),
        format: (catalogForm.format || '').trim(),
        title: (catalogForm.title || '').trim(),
        updatedAt: serverTimestamp(),
      };
      if (editingCatalogId) {
        await updateDoc(doc(firestore, CATALOG_COLLECTION, editingCatalogId), payload);
        setCatalogPuzzles((prev) => prev.map((p) => (p.id === editingCatalogId ? { ...p, ...payload } : p)));
      } else {
        const createdPayload = {
          ...payload,
          createdAt: serverTimestamp(),
        };
        const createdRef = await addDoc(collection(firestore, CATALOG_COLLECTION), createdPayload);
        setCatalogPuzzles((prev) => [...prev, { id: createdRef.id, ...createdPayload }]);
      }
      setCatalogModalOpen(false);
      setEditingCatalogId(null);
      resetCatalogForm();
    } catch (err) {
      setError('Error saving catalog puzzle: ' + err.message);
    }
  };

  const handleCatalogEdit = (puzzle) => {
    setEditingCatalogId(puzzle.id);
    setCatalogForm({
      ...createEmptyPuzzleForm(),
      ...puzzle,
      alternativeAnswers: puzzle.alternativeAnswers || [],
      questions: puzzle.questions || [],
    });
    setCatalogModalOpen(true);
  };

  const handleCatalogDelete = async (catalogId) => {
    if (!window.confirm('Delete this catalog puzzle? Existing event puzzles will not be affected.')) return;
    try {
      await deleteDoc(doc(firestore, CATALOG_COLLECTION, catalogId));
      setCatalogPuzzles((prev) => prev.filter((item) => item.id !== catalogId));
      setCatalogSelection((prev) => {
        const next = new Set(prev);
        next.delete(catalogId);
        return next;
      });
    } catch (err) {
      setError('Error deleting catalog puzzle: ' + err.message);
    }
  };

  const toggleCatalogSelection = (catalogId) => {
    setCatalogSelection((prev) => {
      const next = new Set(prev);
      if (next.has(catalogId)) next.delete(catalogId);
      else next.add(catalogId);
      return next;
    });
  };

  const addSelectedCatalogToEvent = async () => {
    if (!selectedEvent) {
      setError('Select an event first.');
      return;
    }
    const selectedItems = catalogPuzzles.filter((item) => catalogSelection.has(item.id));
    if (!selectedItems.length) {
      window.alert('Select at least one catalog puzzle to add.');
      return;
    }
    try {
      const createdRows = [];
      for (const item of selectedItems) {
        const numberString = (item.number ?? '').toString();
        const seedCode = (item.publicCode || '').trim() || DEFAULT_PUBLIC_CODES[numberString] || '';
        const uniqueCode = await ensureUniquePublicCode(seedCode);
        const payload = {
          number: item.number ?? '',
          internalName: item.internalName || '',
          format: (item.format || '').trim(),
          title: item.title || '',
          value: item.value ?? '',
          tagline: item.tagline || '',
          type: item.type || 'simple',
          location: item.location || '',
          answer: item.answer || '',
          alternativeAnswers: item.alternativeAnswers || [],
          publicCode: uniqueCode,
          onDashboard: !!item.onDashboard,
          dashboardUrl: item.dashboardUrl || '',
          questions: item.questions || [],
          jumpItStations: item.jumpItStations || [],
          jumpItCustomSponsorLocations: (item.jumpItCustomSponsorLocations || []).map(normalizeJumpItCustomLocation),
          enabled: true,
          eventId: selectedEvent,
          catalogPuzzleId: item.id,
          copiedFromCatalogAt: serverTimestamp(),
        };
        const docRef = await addDoc(collection(firestore, 'bonusPuzzles'), payload);
        createdRows.push({ id: docRef.id, ...payload });
      }
      setPuzzles((prev) => [...prev, ...createdRows]);
      setAllBonusPuzzleLinks((prev) => [
        ...prev,
        ...createdRows.map((row) => ({
          id: row.id,
          eventId: row.eventId,
          catalogPuzzleId: row.catalogPuzzleId ? String(row.catalogPuzzleId) : null,
        })),
      ]);
      setCatalogSelection(new Set());
      window.alert(`Added ${createdRows.length} puzzle(s) to this event lineup.`);
    } catch (err) {
      setError('Error adding catalog puzzles to event: ' + err.message);
    }
  };

  const seedCatalogFromExistingPuzzles = async () => {
    if (!window.confirm('Seed catalog from all existing event puzzles? This will skip duplicates by title + type.')) return;
    try {
      const puzzleSnap = await getDocs(collection(firestore, 'bonusPuzzles'));
      const existingCatalog = new Map();
      catalogPuzzles.forEach((item) => existingCatalog.set(toCatalogKey(item), item.id));

      let created = 0;
      for (const puzzleDoc of puzzleSnap.docs) {
        const data = puzzleDoc.data() || {};
        const key = toCatalogKey(data);
        if (!data.title || existingCatalog.has(key)) continue;
        const payload = {
          number: data.number ?? '',
          internalName: data.internalName || data.title || '',
          format: (data.format || '').toString().trim(),
          title: data.title || '',
          value: data.value ?? '',
          tagline: data.tagline || '',
          type: data.type || 'simple',
          location: data.location || '',
          answer: data.answer || '',
          alternativeAnswers: data.alternativeAnswers || [],
          publicCode: data.publicCode || '',
          onDashboard: !!data.onDashboard,
          dashboardUrl: data.dashboardUrl || '',
          questions: data.questions || [],
          jumpItStations: data.jumpItStations || [],
          jumpItCustomSponsorLocations: (data.jumpItCustomSponsorLocations || []).map(normalizeJumpItCustomLocation),
          sourcePuzzleId: puzzleDoc.id,
          sourceEventId: data.eventId || '',
          seededAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        const ref = await addDoc(collection(firestore, CATALOG_COLLECTION), payload);
        existingCatalog.set(key, ref.id);
        created += 1;
      }

      await reloadCatalogAndUsage();
      window.alert(`Catalog seed complete. Added ${created} new catalog puzzle(s).`);
    } catch (err) {
      setError('Error seeding catalog: ' + err.message);
    }
  };

  // Delete a puzzle
  const handleDelete = async (puzzleId) => {
    if (!window.confirm('Are you sure you want to delete this puzzle?')) return;
    try {
      await deleteDoc(doc(firestore, 'bonusPuzzles', puzzleId));
      setPuzzles(puzzles.filter(puzzle => puzzle.id !== puzzleId));
      setAllBonusPuzzleLinks((prev) => prev.filter((r) => r.id !== puzzleId));
    } catch (err) {
      setError('Error deleting puzzle: ' + err.message);
    }
  };

  // Edit: Populate form with puzzle data and open modal
  const handleEdit = (puzzle) => {
    setEditingPuzzleId(puzzle.id);
    setNewPuzzle({
      number: puzzle.number,
      internalName: puzzle.internalName || '',
      format: puzzle.format || '',
      title: puzzle.title,
      value: puzzle.value,
      tagline: puzzle.tagline,
      type: puzzle.type,
      location: puzzle.location,
      answer: puzzle.answer,
      alternativeAnswers: puzzle.alternativeAnswers || [],
      publicCode: puzzle.publicCode || '',
      onDashboard: !!puzzle.onDashboard,
      dashboardUrl: puzzle.dashboardUrl || '',
      questions: puzzle.questions || [],
      catalogPuzzleId: puzzle.catalogPuzzleId || '',
      jumpItStations: puzzle.jumpItStations || [],
      jumpItCustomSponsorLocations: (puzzle.jumpItCustomSponsorLocations || []).map(normalizeJumpItCustomLocation),
    });
    setModalOpen(true);
  };

  const handleToggleEnabled = async (puzzle) => {
    try {
      const puzzleDocRef = doc(firestore, 'bonusPuzzles', puzzle.id);
      await updateDoc(puzzleDocRef, { enabled: !puzzle.enabled });
      setPuzzles(
        puzzles.map(p =>
          p.id === puzzle.id ? { ...p, enabled: !puzzle.enabled } : p
        )
      );
    } catch (err) {
      setError('Error toggling enabled: ' + err.message);
    }
  };

  // Open duplicate modal for a given puzzle
  const handleOpenDuplicateModal = (puzzle) => {
    setDuplicatePuzzle(puzzle);
    // Default duplicate event: current selected event
    setDuplicateEvent(selectedEvent);
    setDuplicateModalOpen(true);
  };

  // Confirm duplication after selecting target event
  const handleConfirmDuplicate = async () => {
    if (!duplicatePuzzle) return;
    try {
      const duplicateData = { ...duplicatePuzzle };
      delete duplicateData.id;
      duplicateData.title = duplicateData.title + ' (Copy)';
      duplicateData.eventId = duplicateEvent;
      duplicateData.publicCode = await ensureUniquePublicCode(duplicateData.publicCode || '');
      const docRef = await addDoc(collection(firestore, 'bonusPuzzles'), duplicateData);
      setAllBonusPuzzleLinks((prev) => [
        ...prev,
        {
          id: docRef.id,
          eventId: duplicateEvent,
          catalogPuzzleId: duplicateData.catalogPuzzleId ? String(duplicateData.catalogPuzzleId) : null,
        },
      ]);
      if (duplicateEvent === selectedEvent) {
        setPuzzles([...puzzles, { id: docRef.id, ...duplicateData }]);
      }
      setDuplicateModalOpen(false);
      setDuplicatePuzzle(null);
    } catch (err) {
      setError('Error duplicating puzzle: ' + err.message);
    }
  };

  const handleCancelDuplicate = () => {
    setDuplicateModalOpen(false);
    setDuplicatePuzzle(null);
  };

  useModalEscapeKey(!!printSheetPuzzle, () => setPrintSheetPuzzle(null));
  useModalEscapeKey(duplicateModalOpen, handleCancelDuplicate);
  useModalEscapeKey(catalogModalOpen, () => setCatalogModalOpen(false));
  useModalEscapeKey(modalOpen, () => setModalOpen(false));

  // Generate printable sheet for a bonus puzzle (logo, title, QR code, "Bonus clue #N")
  const getPrintSheetHtml = (puzzle, format) => {
    const baseUrl = window.location.origin;
    const logoUrl = `${baseUrl}/fresh-meet-logo.png`;
    // Dashboard puzzles: use user-entered dashboard URL for QR (normalized so localhost → current origin); others use /p/publicCode
    const dashboardUrlTrimmed = puzzle.dashboardUrl && puzzle.dashboardUrl.trim();
    const puzzleUrl = puzzle.onDashboard && dashboardUrlTrimmed
      ? normalizePuzzleUrl(dashboardUrlTrimmed)
      : puzzle.publicCode
        ? `${baseUrl}/p/${puzzle.publicCode}`
        : '';
    const qrSize = format === 'business' ? 400 : 200;
    const qrSrc = puzzleUrl
      ? `https://api.qrserver.com/v1/create-qr-code/?size=${qrSize}x${qrSize}&data=${encodeURIComponent(puzzleUrl)}`
      : '';
    const puzzleTitle = puzzle.title || `Puzzle ${puzzle.number}`;
    const subtitle = `Bonus clue #${puzzle.number}`;
    const logoHtml = `<img src="${logoUrl}" alt="Fresh Meet" class="print-logo-img" />`;

    const locationText = (puzzle.location || '').toString().trim();
    const oneCard = `
      <div class="print-card">
        ${logoHtml}
        <h2 class="print-title">${puzzleTitle}</h2>
        <p class="print-subtitle">${subtitle}</p>
        ${locationText ? `<p class="print-location">Location: ${locationText}</p>` : ''}
        ${qrSrc ? `<img src="${qrSrc}" alt="QR Code" class="print-qr" />` : '<p class="print-no-qr">No public code — add one to show QR</p>'}
      </div>
    `;
    const landscapeCard = `
      <div class="print-card print-card-landscape">
        <p class="print-subtitle">${subtitle}</p>
        ${locationText ? `<p class="print-location">Location: ${locationText}</p>` : ''}
        ${qrSrc ? `<img src="${qrSrc}" alt="QR Code" class="print-qr" />` : '<p class="print-no-qr">No public code — add one to show QR</p>'}
      </div>
    `;

    const cardCount = format === 'full' ? 1 : format === 'half' ? 2 : format === 'landscape' ? 1 : 10;
    const cardsHtml = format === 'landscape' ? landscapeCard : Array(cardCount).fill(oneCard).join('');

    const pageClass = format === 'full' ? 'print-full' : format === 'half' ? 'print-half' : format === 'landscape' ? 'print-landscape' : 'print-business';
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${puzzleTitle} - Fresh Meet</title>
  <style>
    * { box-sizing: border-box; }
    ${format === 'landscape' ? '@page { size: 11in 8.5in; margin: 0.25in; }' : '@page { size: 8.5in 11in; margin: 0.5in; }'}
    body { margin: 0; font-family: 'Segoe UI', system-ui, sans-serif; background: #fff7ed; }
    .print-page { width: 7.5in; height: 10in; margin: 0; padding: 0; overflow: hidden; }
    .print-card {
      text-align: center;
      padding: 2rem 1.5rem;
      background: linear-gradient(180deg, #ffffff 0%, #fff7ed 100%);
      border: 2px solid #ea580c;
      border-radius: 12px;
      box-shadow: 0 4px 12px rgba(234, 88, 12, 0.2);
    }
    .print-logo-img { display: block; max-width: 180px; height: auto; margin: 0 auto 1rem; }
    .print-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: #9a3412;
      margin: 0 0 0.35rem 0;
      line-height: 1.25;
    }
    .print-subtitle {
      font-size: 1rem;
      color: #ea580c;
      font-weight: 600;
      letter-spacing: 0.03em;
      margin: 0 0 1.25rem 0;
    }
    .print-location {
      font-size: 0.95rem;
      color: #444;
      margin: 0 0 1rem 0;
    }
    .print-qr {
      display: block;
      width: 200px;
      height: 200px;
      margin: 0 auto;
      padding: 8px;
      background: #fff;
      border-radius: 8px;
      border: 1px solid #fed7aa;
    }
    .print-no-qr { color: #78716c; font-size: 0.9rem; margin: 0; }
    /* Full sheet: content fits in 7.5x10in printable area */
    .print-full .print-page { background: #fff7ed; box-sizing: border-box; }
    .print-full .print-card {
      width: 7.5in;
      height: 10in;
      max-width: 7.5in;
      max-height: 10in;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 0.4in;
      border-radius: 0;
      border: none;
      box-sizing: border-box;
    }
    .print-full .print-logo-img { max-width: 280px; margin-bottom: 1.5rem; }
    .print-full .print-title { font-size: 2.5rem; }
    .print-full .print-subtitle { font-size: 1.5rem; margin-bottom: 0.5rem; }
    .print-full .print-location { font-size: 1.25rem; margin-bottom: 1.5rem; }
    .print-full .print-qr { width: 280px; height: 280px; }
    /* Half sheet: two cards inside 7.5x10in printable area */
    .print-half .print-page {
      display: flex;
      flex-direction: column;
      gap: 0.25in;
      align-items: center;
      justify-content: space-evenly;
      background: #fff7ed;
      box-sizing: border-box;
    }
    .print-half .print-card {
      width: 7.5in;
      flex: 1 1 0;
      min-height: 0;
      max-height: 4.7in;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 0.2rem 0.5rem;
      box-sizing: border-box;
    }
    .print-half .print-logo-img { max-width: 120px; max-height: 48px; object-fit: contain; margin-bottom: 0.35rem; }
    .print-half .print-title { font-size: 1.1rem; margin-bottom: 0.2rem; }
    .print-half .print-subtitle { font-size: 0.85rem; margin-bottom: 0.2rem; }
    .print-half .print-location { font-size: 0.8rem; margin-bottom: 0.4rem; }
    .print-half .print-qr { width: 140px; height: 140px; flex-shrink: 0; }
    /* Business cards: 10 cards inside 7.5x10in printable area (2 cols x 5 rows, ~3.5" x 1.95" each) */
    .print-business .print-page {
      display: grid;
      grid-template-columns: 3.5in 3.5in;
      grid-template-rows: 1.95in 1.95in 1.95in 1.95in 1.95in;
      column-gap: 0.25in;
      row-gap: 0.05in;
      align-items: stretch;
      justify-items: stretch;
      background: #fff7ed;
      box-sizing: border-box;
    }
    .print-business .print-card {
      width: 3.5in;
      height: 1.95in;
      min-width: 0;
      min-height: 0;
      max-width: 3.5in;
      max-height: 1.95in;
      padding: 0.06in;
      font-size: 0.7rem;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      box-sizing: border-box;
    }
    .print-business .print-logo-img { max-width: 0.45in; max-height: 0.28in; margin-bottom: 0.02in; object-fit: contain; flex-shrink: 0; }
    .print-business .print-title { font-size: 0.45rem; margin: 0 0 0.02in 0; line-height: 1.15; flex-shrink: 0; }
    .print-business .print-subtitle { font-size: 0.4rem; margin: 0 0 0.02in 0; flex-shrink: 0; }
    .print-business .print-location { font-size: 0.35rem; margin: 0 0 0.03in 0; flex-shrink: 0; }
    .print-business .print-qr { width: 1.08in; height: 1.08in; min-width: 1.08in; min-height: 1.08in; padding: 2px; flex-shrink: 0; }
    /* Single sheet landscape: 8.5x11 landscape, no title, tiny font */
    .print-landscape .print-page {
      width: 11in; height: 8.5in; max-width: 11in; max-height: 8.5in; min-height: 8.5in;
      display: flex; align-items: center; justify-content: center; background: #fff; box-sizing: border-box;
    }
    .print-landscape .print-card-landscape {
      text-align: center; padding: 0.2in; border: 1px solid #ddd; border-radius: 6px;
      font-size: 8px; line-height: 1.3;
    }
    .print-landscape .print-card-landscape .print-subtitle { font-size: 9px; font-weight: 600; margin: 0 0 0.1in 0; color: #333; }
    .print-landscape .print-card-landscape .print-location { font-size: 8px; margin: 0 0 0.15in 0; color: #555; }
    .print-landscape .print-card-landscape .print-qr { width: 1.8in; height: 1.8in; margin: 0.1in auto; display: block; }
    .print-landscape .print-card-landscape .print-no-qr { font-size: 7px; margin: 0; color: #888; }
    @media print {
      body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; background: #fff; }
      .print-page {
        width: 7.5in !important;
        height: 10in !important;
        max-width: 7.5in !important;
        max-height: 10in !important;
        min-height: 10in !important;
      }
      .print-landscape .print-page {
        width: 11in !important; height: 8.5in !important;
        max-width: 11in !important; max-height: 8.5in !important;
        min-height: 8.5in !important;
      }
      .print-full .print-page { background: #fff7ed !important; }
      .print-half .print-page { background: #fff7ed !important; }
      .print-business .print-page { background: #fff !important; }
      .print-landscape .print-page { background: #fff !important; }
      .print-card { box-shadow: none; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body class="${pageClass}">
  <div class="no-print" style="position:fixed;top:10px;right:10px;z-index:9999;">
    <button onclick="window.print()" style="padding:8px 16px;margin-right:8px;cursor:pointer;background:#ea580c;color:#fff;border:none;border-radius:6px;">Print</button>
    <button onclick="window.close()" style="padding:8px 16px;cursor:pointer;background:#78716c;color:#fff;border:none;border-radius:6px;">Close</button>
  </div>
  <div class="print-page">${cardsHtml}</div>
</body>
</html>`;
  };

  const handleOpenPrintSheet = (puzzle) => {
    setPrintSheetPuzzle(puzzle);
  };

  const handleGeneratePrintSheet = (format) => {
    if (!printSheetPuzzle) return;
    const html = getPrintSheetHtml(printSheetPuzzle, format);
    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    setPrintSheetPuzzle(null);
  };

  // Export puzzles as CSV
  const handleExportCSV = () => {
    if (puzzles.length === 0) {
      alert('No puzzles to export');
      return;
    }

    // Get the base URL from the current window location
    const baseUrl = window.location.origin;

    // Create CSV header
    const header = ['Puzzle #', 'Title', 'Type', 'Value', 'Answer', 'Public Code', 'Full URL'];
    
    // Create CSV rows
    const rows = puzzles.map(puzzle => [
      puzzle.number,
      `"${puzzle.title || ''}"`,
      puzzle.type,
      puzzle.value,
      `"${puzzle.answer || ''}"`,
      puzzle.publicCode || '',
      puzzle.publicCode ? `${baseUrl}/p/${puzzle.publicCode}` : ''
    ]);

    // Combine header and rows
    const csvContent = [header, ...rows]
      .map(row => row.join(','))
      .join('\n');

    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `bonus-puzzles-${selectedEvent || 'all'}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export puzzles as printable HTML table (single sheet landscape) + QR grid page
  const handleExportPrintable = () => {
    if (puzzles.length === 0) {
      alert('No puzzles to export');
      return;
    }

    const baseUrl = window.location.origin;
    const tableRowsHtml = puzzles
      .slice()
      .sort((a, b) => Number(a.number) - Number(b.number))
      .map((puzzle) => `
              <tr>
                <td>${puzzle.number}</td>
                <td>${(puzzle.title || '').replace(/</g, '&lt;')}</td>
                <td>${puzzle.value ?? ''}</td>
                <td>${(puzzle.location || '').replace(/</g, '&lt;')}</td>
                <td>${(puzzle.answer || '').replace(/</g, '&lt;')}</td>
                <td>${(puzzle.publicCode || '').replace(/</g, '&lt;')}</td>
              </tr>
            `)
      .join('');

    const qrGridHtml = puzzles
      .slice()
      .sort((a, b) => Number(a.number) - Number(b.number))
      .map((puzzle) => {
        const loc = (puzzle.location || '').replace(/</g, '&lt;');
        const title = (puzzle.title || '').replace(/</g, '&lt;');
        const dashboardUrlTrimmed = (puzzle.dashboardUrl || '').trim();
        const rawUrl = puzzle.onDashboard && dashboardUrlTrimmed
          ? normalizePuzzleUrl(dashboardUrlTrimmed)
          : puzzle.publicCode
            ? `${baseUrl}/p/${puzzle.publicCode}`
            : '';
        if (!rawUrl) {
          return '';
        }
        const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(rawUrl)}`;
        return `
                  <div class="qr-grid-item">
                    <img src="${qrSrc}" alt="QR for puzzle ${puzzle.number}" />
                    <div class="qr-grid-meta">
                      <span class="qr-grid-number">#${puzzle.number}</span>
                      <span class="qr-grid-title-text">${title}</span>
                      <span class="qr-grid-location">${loc}</span>
                    </div>
                  </div>
                `;
      })
      .join('');

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Bonus Puzzles</title>
        <style>
          * { box-sizing: border-box; }
          @page { size: 11in 8.5in; margin: 0.25in; }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0.25in;
            font-size: 10px;
            background: #fff;
          }
          .no-print { margin-bottom: 8px; }
          .no-print button {
            padding: 6px 12px;
            margin-right: 8px;
            cursor: pointer;
            background: #ea580c;
            color: #fff;
            border: none;
            border-radius: 4px;
          }
          .no-print button:last-of-type { background: #666; }
          table {
            border-collapse: collapse;
            width: 100%;
            font-size: 10px;
            line-height: 1.25;
            table-layout: fixed;
          }
          th, td {
            border: 1px solid #ccc;
            padding: 3px 5px;
            text-align: left;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          th {
            background-color: #4CAF50;
            color: white;
            font-weight: 600;
          }
          tr:nth-child(even) { background-color: #f5f5f5; }
          /* Column widths: # narrow, Title wide, Value narrow, Location/Answer/Code proportional */
          th:nth-child(1), td:nth-child(1) { width: 4%; min-width: 0; }
          th:nth-child(2), td:nth-child(2) { width: 28%; }
          th:nth-child(3), td:nth-child(3) { width: 6%; min-width: 0; }
          th:nth-child(4), td:nth-child(4) { width: 18%; }
          th:nth-child(5), td:nth-child(5) { width: 20%; }
          th:nth-child(6), td:nth-child(6) { width: 24%; word-break: break-all; }

          /* QR grid page */
          .qr-grid-page {
            page-break-before: always;
            padding-top: 0.25in;
          }
          .qr-grid-title {
            font-size: 14px;
            font-weight: 600;
            margin: 0 0 8px 0;
          }
          .qr-grid-subtitle {
            font-size: 10px;
            color: #555;
            margin: 0 0 8px 0;
          }
          .qr-grid {
            display: grid;
            grid-template-columns: repeat(5, 1fr);
            gap: 8px;
          }
          .qr-grid-item {
            border: 1px solid #ddd;
            border-radius: 4px;
            padding: 4px;
            text-align: center;
            font-size: 9px;
          }
          .qr-grid-item img {
            display: block;
            width: 100%;
            max-width: 120px;
            height: auto;
            margin: 0 auto 4px;
          }
          .qr-grid-meta {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }
          .qr-grid-meta span {
            display: block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .qr-grid-meta .qr-grid-number {
            font-weight: 600;
          }
          .qr-grid-meta .qr-grid-title-text {
            font-weight: 500;
          }
          .qr-grid-meta .qr-grid-location {
            font-size: 8px;
            color: #666;
          }
          @media print {
            body { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .no-print { display: none !important; }
            .print-sheet {
              width: 11in; height: 8.5in;
              overflow: hidden;
              position: relative;
            }
            table { font-size: 11px; }
            th, td { padding: 2px 4px; }
          }
        </style>
      </head>
      <body class="print-sheet">
        <div class="no-print">
          <button onclick="window.print()">Print</button>
          <button onclick="window.close()">Close</button>
          <span style="margin-left:12px;font-size:12px;color:#555;">Set orientation to Landscape for one sheet.</span>
        </div>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Title</th>
              <th>Value</th>
              <th>Location</th>
              <th>Answer</th>
              <th>Public Code</th>
            </tr>
          </thead>
          <tbody>
            ${tableRowsHtml}
          </tbody>
        </table>

        <!-- Second page: grid of QR codes for each puzzle -->
        <div class="qr-grid-page">
          <h3 class="qr-grid-title">Bonus puzzle QR codes</h3>
          <p class="qr-grid-subtitle">Scan any QR to open that puzzle’s public URL.</p>
          <div class="qr-grid">
            ${qrGridHtml}
          </div>
        </div>
      </body>
      </html>
    `;

    // Open in new window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  return (
    <div className="bonus-puzzle-management">
      <datalist id="bonus-puzzle-format-presets">
        {BONUS_PUZZLE_FORMAT_PRESETS.map((opt) => (
          <option key={opt} value={opt} />
        ))}
      </datalist>
      <h2>Bonus Puzzle Management</h2>
      {error && <p className="error">{error}</p>}

      {/* Dropdown for active events */}
      <div className="dropdown-container">
        <label htmlFor="activeEvent">Select Active Event:</label>
        <select id="activeEvent" value={selectedEvent} onChange={handleActiveEventChange}>
          {activeEvents.map(event => (
            <option key={event.id} value={event.id}>
              {event.title}
            </option>
          ))}
        </select>
      </div>

      <div className="bonus-tab-nav">
        <button
          type="button"
          className={`bonus-tab-button ${activeTab === 'event' ? 'active' : ''}`}
          onClick={() => setActiveTab('event')}
        >
          Current Event Puzzles
        </button>
        <button
          type="button"
          className={`bonus-tab-button ${activeTab === 'catalog' ? 'active' : ''}`}
          onClick={() => setActiveTab('catalog')}
        >
          Puzzle Catalog
        </button>
      </div>

      {/* Button to open modal for adding a puzzle */}
      {activeTab === 'event' && selectedEvent && puzzles.length > 0 && (
        <div className="export-buttons">
          <button type="button" onClick={handleExportCSV} className="bonus-export-button">
            📥 Export as CSV
          </button>
          <button type="button" onClick={handleExportPrintable} className="bonus-export-button">
            🖨️ Export Printable Table
          </button>
          <button
            type="button"
            onClick={() => openPuzzleTableExport('print')}
            disabled={!puzzleTableExportRows.length}
            className="bonus-export-button btn-fm-print-outline"
          >
            Print table
          </button>
          <AdminCopyMenu
            ariaLabel="Copy bonus puzzles table"
            disabled={!puzzleTableExportRows.length}
            items={[
              {
                label: 'Copy table…',
                showCopiedFeedback: false,
                onSelect: () => openPuzzleTableExport('copy'),
              },
            ]}
          />
        </div>
      )}

      {activeTab === 'event' && (
      <button
        onClick={() => {
          setModalOpen(true);
          setEditingPuzzleId(null);
          resetPuzzleForm();
        }}
        className="add-puzzle-button"
      >
        Add Bonus Puzzle
      </button>
      )}

      {/* Table of bonus puzzles */}
      {activeTab === 'event' && (
      <div className="table-container">
        {loading ? (
          <p>Loading puzzles...</p>
        ) : sortedPuzzles.length ? (
          <table className="puzzle-table">
            <thead>
              <tr>
                <th>Number</th>
                <th>Internal name</th>
                <th>Format</th>
                <th>Title</th>
                <th>Value</th>
                <th>Tagline</th>
                <th>Type</th>
                <th>Location</th>
                <th>Answer</th>
                <th>Public Code</th>
                <th>On dashboard</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedPuzzles.map(puzzle => (
                <tr key={puzzle.id}>
                  <td>{puzzle.number}</td>
                  <td>{puzzle.internalName || '—'}</td>
                  <td>{puzzle.format || '—'}</td>
                  <td>{puzzle.title}</td>
                  <td>{puzzle.value}</td>
                  <td>{puzzle.tagline}</td>
                  <td>{puzzle.type}</td>
                  <td>{puzzle.location}</td>
                  <td>{puzzle.answer}</td>
                  <td>
                    {puzzle.publicCode ? (
                      <a href={puzzle.onDashboard && (puzzle.dashboardUrl || '').trim() ? normalizePuzzleUrl((puzzle.dashboardUrl || '').trim()) : `/p/${puzzle.publicCode}`} target="_blank" rel="noopener noreferrer">
                        {puzzle.publicCode}
                      </a>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td>{puzzle.onDashboard ? 'Yes' : 'No'}</td>
                  <td>{puzzle.enabled ? 'Enabled' : 'Disabled'}</td>
                  <td>
                    <button type="button" className="bp-row-action bp-row-action-edit" onClick={() => handleEdit(puzzle)}>Edit</button>
                    <button type="button" className="bp-row-action bp-row-action-neutral" onClick={() => handleOpenDuplicateModal(puzzle)}>Duplicate</button>
                    <button type="button" className="bp-row-action bp-row-action-neutral" onClick={() => handleOpenPrintSheet(puzzle)} title="Generate printable sheet with logo, QR code, and subtitle">
                      Print sheet
                    </button>
                    <button type="button" className="bp-row-action bp-row-action-neutral" onClick={() => handleToggleEnabled(puzzle)}>
                      {puzzle.enabled ? 'Disable' : 'Enable'}
                    </button>
                    <button type="button" className="bp-row-action bp-row-action-delete" onClick={() => handleDelete(puzzle.id)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>No bonus puzzles found for this event.</p>
        )}
      </div>
      )}

      {activeTab === 'catalog' && (
        <div className="catalog-panel">
          <div className="catalog-panel-header">
            <h3>Puzzle Catalog (Reusable Database)</h3>
            <div className="catalog-actions">
              <button
                type="button"
                onClick={() => {
                  setCatalogModalOpen(true);
                  setEditingCatalogId(null);
                  resetCatalogForm();
                }}
              >
                Add to Catalog
              </button>
              <button type="button" onClick={reloadCatalogAndUsage} disabled={catalogUsageLoading}>
                {catalogUsageLoading ? 'Refreshing…' : 'Refresh catalog & years'}
              </button>
              <button type="button" onClick={seedCatalogFromExistingPuzzles}>
                Seed Catalog From Existing Events
              </button>
              <button type="button" onClick={addSelectedCatalogToEvent} disabled={!catalogSelection.size || !selectedEvent}>
                Add Selected To This Event
              </button>
            </div>
          </div>
          {catalogPuzzles.length > 0 && (
            <div className="catalog-filters">
              <div className="catalog-filter-row">
                <label className="catalog-filter-label">
                  Search
                  <input
                    type="search"
                    placeholder="Title, internal name, format, location…"
                    value={catalogSearchQuery}
                    onChange={(e) => setCatalogSearchQuery(e.target.value)}
                    autoComplete="off"
                  />
                </label>
                <label className="catalog-filter-label">
                  Type
                  <select value={catalogFilterType} onChange={(e) => setCatalogFilterType(e.target.value)}>
                    <option value="">All types</option>
                    {BONUS_PUZZLE_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </label>
                <label className="catalog-filter-label">
                  Format
                  <select value={catalogFilterFormat} onChange={(e) => setCatalogFilterFormat(e.target.value)}>
                    <option value="">All formats</option>
                    {catalogDistinctFormats.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </label>
                <label className="catalog-filter-label">
                  Year used
                  <select value={catalogFilterYear} onChange={(e) => setCatalogFilterYear(e.target.value)}>
                    <option value="">Any year</option>
                    <option value="never">Never used</option>
                    {catalogDistinctYears.map((y) => (
                      <option key={y} value={String(y)}>{y}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="catalog-filter-row">
                <label className="catalog-filter-label">
                  Sort by
                  <select value={catalogSortKey} onChange={(e) => setCatalogSortKey(e.target.value)}>
                    <option value="title">Title (A–Z)</option>
                    <option value="internalName">Internal name (A–Z)</option>
                    <option value="type">Type (A–Z)</option>
                    <option value="format">Format (A–Z)</option>
                    <option value="usesDesc">Times used (high → low)</option>
                    <option value="usesAsc">Times used (low → high)</option>
                    <option value="yearsDesc">Most recent year first</option>
                  </select>
                </label>
                <label className="catalog-filter-checkbox">
                  <input
                    type="checkbox"
                    checked={catalogHideUsedInSelectedEvent}
                    onChange={(e) => setCatalogHideUsedInSelectedEvent(e.target.checked)}
                  />
                  Hide puzzles already used in selected event
                </label>
                <button
                  type="button"
                  className="catalog-clear-filters"
                  onClick={() => {
                    setCatalogSearchQuery('');
                    setCatalogFilterType('');
                    setCatalogFilterFormat('');
                    setCatalogFilterYear('');
                    setCatalogHideUsedInSelectedEvent(false);
                    setCatalogSortKey('title');
                  }}
                >
                  Clear filters
                </button>
              </div>
              <p className="catalog-filter-summary">
                Showing {filteredSortedCatalogPuzzles.length} of {catalogPuzzles.length} catalog puzzle(s).
              </p>
            </div>
          )}
          {catalogPuzzles.length ? (
            filteredSortedCatalogPuzzles.length ? (
            <div className="table-container">
              <table className="puzzle-table">
                <thead>
                  <tr>
                    <th>Select</th>
                    <th>Internal name</th>
                    <th>Format</th>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Value</th>
                    <th>Default #</th>
                    <th>Years used</th>
                    <th>Times used</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSortedCatalogPuzzles.map((catalogPuzzle) => {
                    const usage = catalogUsageByCatalogId.get(catalogPuzzle.id);
                    const yearList = usage ? [...usage.years].sort((a, b) => b - a) : [];
                    const timesUsed = usage ? usage.eventIds.size : 0;
                    const yearsLabel = timesUsed === 0 ? '—' : (yearList.length ? yearList.join(', ') : 'Unknown year');
                    const yearsTitle = timesUsed ? `${timesUsed} event lineup(s)` : '';
                    return (
                    <tr key={catalogPuzzle.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={catalogSelection.has(catalogPuzzle.id)}
                          onChange={() => toggleCatalogSelection(catalogPuzzle.id)}
                        />
                      </td>
                      <td>{catalogPuzzle.internalName || '—'}</td>
                      <td>{catalogPuzzle.format || '—'}</td>
                      <td>{catalogPuzzle.title}</td>
                      <td>{catalogPuzzle.type}</td>
                      <td>{catalogPuzzle.value}</td>
                      <td>{catalogPuzzle.number}</td>
                      <td title={yearsTitle}>{yearsLabel}</td>
                      <td>{timesUsed}</td>
                      <td>
                        <button type="button" className="bp-row-action bp-row-action-edit" onClick={() => handleCatalogEdit(catalogPuzzle)}>Edit</button>
                        <button type="button" className="bp-row-action bp-row-action-delete" onClick={() => handleCatalogDelete(catalogPuzzle.id)}>Delete</button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            ) : (
              <p>No catalog puzzles match the current filters.</p>
            )
          ) : (
            <p>No catalog puzzles yet. Add one manually or seed from historical event puzzles.</p>
          )}
        </div>
      )}

      {/* Modal for add/edit bonus puzzle */}
      {modalOpen && (
        <div className="fm-modal-overlay" role="presentation" onClick={() => setModalOpen(false)}>
          <div
            className="fm-modal fm-modal--lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bp-edit-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="bp-edit-modal-title">
                {editingPuzzleId ? 'Edit bonus puzzle' : 'Add bonus puzzle'}
              </h2>
              <button type="button" className="fm-modal-close" onClick={() => setModalOpen(false)} aria-label="Close">
                ✕
              </button>
            </div>
            <form noValidate onSubmit={handleSubmit} className="puzzle-form">
              <div className="fm-modal-body">
              <div className="form-group">
                <label>Number:</label>
                <input
                  name="bpNumber"
                  type="text"
                  value={newPuzzle.number}
                  onChange={(e) => setNewPuzzle({ ...newPuzzle, number: e.target.value })}
                  required
                />
                <ModalFieldRequiredError invalidNames={bonusPuzzleFormInvalidNames} fieldName="bpNumber" />
              </div>
              <div className="form-group">
                <label>Internal Name:</label>
                <input
                  type="text"
                  value={newPuzzle.internalName}
                  onChange={(e) => setNewPuzzle({ ...newPuzzle, internalName: e.target.value })}
                  placeholder="Internal/admin-only label"
                />
              </div>
              <div className="form-group">
                <label>Format:</label>
                <input
                  type="text"
                  value={newPuzzle.format}
                  onChange={(e) => setNewPuzzle({ ...newPuzzle, format: e.target.value })}
                  list="bonus-puzzle-format-presets"
                  placeholder="e.g. Print, Digital / URL"
                />
              </div>
              <div className="form-group">
                <label>Title:</label>
                <input
                  name="bpTitle"
                  type="text"
                  value={newPuzzle.title}
                  onChange={(e) => setNewPuzzle({ ...newPuzzle, title: e.target.value })}
                  required
                />
                <ModalFieldRequiredError invalidNames={bonusPuzzleFormInvalidNames} fieldName="bpTitle" />
                <small className="form-hint">Shown publicly on dashboards and puzzle pages.</small>
              </div>
              <div className="form-group">
                <label>Value:</label>
                <input
                  name="bpValue"
                  type="text"
                  value={newPuzzle.value}
                  onChange={(e) => setNewPuzzle({ ...newPuzzle, value: e.target.value })}
                  required
                />
                <ModalFieldRequiredError invalidNames={bonusPuzzleFormInvalidNames} fieldName="bpValue" />
              </div>
              <div className="form-group">
                <label>Tagline:</label>
                <input
                  name="bpTagline"
                  type="text"
                  value={newPuzzle.tagline}
                  onChange={(e) => setNewPuzzle({ ...newPuzzle, tagline: e.target.value })}
                  required
                />
                <ModalFieldRequiredError invalidNames={bonusPuzzleFormInvalidNames} fieldName="bpTagline" />
              </div>
              <div className="form-group">
                <label>Type:</label>
                <select
                  name="bpType"
                  value={newPuzzle.type}
                  onChange={(e) => setNewPuzzle({ ...newPuzzle, type: e.target.value })}
                  required
                >
                  {BONUS_PUZZLE_TYPES.map(type => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <ModalFieldRequiredError invalidNames={bonusPuzzleFormInvalidNames} fieldName="bpType" />
              </div>
              <div className="form-group">
                <label>Location:</label>
                <input
                  type="text"
                  value={newPuzzle.location}
                  onChange={(e) => setNewPuzzle({ ...newPuzzle, location: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Answer:</label>
                <input
                  type="text"
                  value={newPuzzle.answer}
                  onChange={(e) => setNewPuzzle({ ...newPuzzle, answer: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Alternative answers (optional):</label>
                <textarea
                  rows="3"
                  placeholder="Enter alternative correct answers, one per line or separated by commas"
                  value={(newPuzzle.alternativeAnswers || []).join('\n')}
                  onChange={(e) =>
                    setNewPuzzle({
                      ...newPuzzle,
                      alternativeAnswers: parseAlternativeAnswers(e.target.value),
                    })
                  }
                />
                <small className="form-hint">
                  Answers are matched case-insensitively. Any listed alternative will be accepted as correct.
                </small>
              </div>
              <div className="form-group">
                <label>Public Code:</label>
                <input
                  type="text"
                  value={newPuzzle.publicCode}
                  onChange={(e) => setNewPuzzle({ ...newPuzzle, publicCode: e.target.value })}
                  placeholder="Auto-generated if left blank"
                />
              </div>
              <div className="form-group form-group-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={!!newPuzzle.onDashboard}
                    onChange={(e) => setNewPuzzle({ ...newPuzzle, onDashboard: e.target.checked })}
                  />
                  {' '}Include on main dashboard
                </label>
              </div>
              {newPuzzle.onDashboard && (
                <div className="form-group">
                  <label>Dashboard / interact URL (for print sheet QR and link):</label>
                  <input
                    type="url"
                    value={newPuzzle.dashboardUrl || ''}
                    onChange={(e) => setNewPuzzle({ ...newPuzzle, dashboardUrl: e.target.value })}
                    placeholder="https://..."
                  />
                  <small className="form-hint">Enter the URL used on the printable sheet QR code and when opening this puzzle from the dashboard.</small>
                </div>
              )}
              
              {/* Quiz Questions Editor */}
              {newPuzzle.type === 'quiz' && (
                <QuizQuestionsEditor
                  questions={newPuzzle.questions || []}
                  onChange={(questions) => setNewPuzzle({ ...newPuzzle, questions })}
                />
              )}

              {/* Video Submission fields */}
              {newPuzzle.type === 'videoSubmission' && (
                <>
                  <div className="form-group">
                    <label>Base points (per adequate video):</label>
                    <input
                      name="bpBasePoints"
                      type="number"
                      min="0"
                      value={newPuzzle.basePoints}
                      onChange={(e) => setNewPuzzle({ ...newPuzzle, basePoints: e.target.value })}
                      required
                    />
                    <small className="form-hint">Awarded to every approved video regardless of creativity bonus.</small>
                  </div>
                  <div className="form-group">
                    <label>Max creativity bonus points:</label>
                    <input
                      name="bpCreativityMax"
                      type="number"
                      min="0"
                      value={newPuzzle.creativityMaxPoints}
                      onChange={(e) => setNewPuzzle({ ...newPuzzle, creativityMaxPoints: e.target.value })}
                      required
                    />
                    <small className="form-hint">Judge assigns 0–max extra points per video. Teams can earn up to (base + max) × 3 from this puzzle.</small>
                  </div>
                </>
              )}

              {/* Jump It or Fix It fields */}
              {newPuzzle.type === 'jumpItOrFixIt' && (
                <div className="form-group">
                  <label>Jump It or Fix It — stations</label>
                  <JumpItOrFixItStationsAdminEditor
                    stations={newPuzzle.jumpItStations || []}
                    onChange={(stations) => setNewPuzzle({ ...newPuzzle, jumpItStations: stations })}
                    customLocations={newPuzzle.jumpItCustomSponsorLocations || []}
                    onCustomLocationsChange={(jumpItCustomSponsorLocations) =>
                      setNewPuzzle({ ...newPuzzle, jumpItCustomSponsorLocations })
                    }
                    mergedSponsors={jumpItMergedSponsors}
                    sponsorsLoading={jumpItSponsorsLoading}
                    publicCode={newPuzzle.publicCode}
                    eventId={selectedEvent}
                    printEventTitle={activeEvents.find((e) => e.id === selectedEvent)?.title || ''}
                    printPuzzleTitle={
                      (newPuzzle.title || '').trim() ||
                      (newPuzzle.number !== '' && newPuzzle.number != null
                        ? `Bonus puzzle ${newPuzzle.number}`
                        : '')
                    }
                    printFooterLeft={
                      (newPuzzle.location || '').trim() ||
                      activeEvents.find((e) => e.id === selectedEvent)?.title ||
                      ''
                    }
                  />
                </div>
              )}

              </div>
              <div className="fm-modal-footer">
                <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={() => setModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="fm-modal-btn fm-modal-btn-primary">
                  {editingPuzzleId ? 'Update puzzle' : 'Add puzzle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {catalogModalOpen && (
        <div
          className="fm-modal-overlay"
          role="presentation"
          onClick={() => {
            setCatalogModalOpen(false);
            setEditingCatalogId(null);
            resetCatalogForm();
          }}
        >
          <div
            className="fm-modal fm-modal--lg"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bp-catalog-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="bp-catalog-modal-title">
                {editingCatalogId ? 'Edit catalog puzzle' : 'Add catalog puzzle'}
              </h2>
              <button
                type="button"
                className="fm-modal-close"
                onClick={() => {
                  setCatalogModalOpen(false);
                  setEditingCatalogId(null);
                  resetCatalogForm();
                }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <form noValidate onSubmit={saveCatalogPuzzle} className="puzzle-form">
              <div className="fm-modal-body">
              <div className="form-group">
                <label>Number:</label>
                <input
                  name="catNumber"
                  type="text"
                  value={catalogForm.number}
                  onChange={(e) => setCatalogForm({ ...catalogForm, number: e.target.value })}
                  required
                />
                <ModalFieldRequiredError invalidNames={catalogPuzzleFormInvalidNames} fieldName="catNumber" />
              </div>
              <div className="form-group">
                <label>Internal Name:</label>
                <input
                  type="text"
                  value={catalogForm.internalName}
                  onChange={(e) => setCatalogForm({ ...catalogForm, internalName: e.target.value })}
                  placeholder="Internal/admin-only label"
                />
              </div>
              <div className="form-group">
                <label>Format:</label>
                <input
                  type="text"
                  value={catalogForm.format}
                  onChange={(e) => setCatalogForm({ ...catalogForm, format: e.target.value })}
                  list="bonus-puzzle-format-presets"
                  placeholder="e.g. Print, Digital / URL"
                />
              </div>
              <div className="form-group">
                <label>Title:</label>
                <input
                  name="catTitle"
                  type="text"
                  value={catalogForm.title}
                  onChange={(e) => setCatalogForm({ ...catalogForm, title: e.target.value })}
                  required
                />
                <ModalFieldRequiredError invalidNames={catalogPuzzleFormInvalidNames} fieldName="catTitle" />
                <small className="form-hint">Shown publicly when this puzzle is on an event lineup.</small>
              </div>
              <div className="form-group">
                <label>Value:</label>
                <input
                  name="catValue"
                  type="text"
                  value={catalogForm.value}
                  onChange={(e) => setCatalogForm({ ...catalogForm, value: e.target.value })}
                  required
                />
                <ModalFieldRequiredError invalidNames={catalogPuzzleFormInvalidNames} fieldName="catValue" />
              </div>
              <div className="form-group">
                <label>Type:</label>
                <select
                  name="catType"
                  value={catalogForm.type}
                  onChange={(e) => setCatalogForm({ ...catalogForm, type: e.target.value })}
                  required
                >
                  {BONUS_PUZZLE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
                <ModalFieldRequiredError invalidNames={catalogPuzzleFormInvalidNames} fieldName="catType" />
              </div>
              <div className="form-group">
                <label>Tagline:</label>
                <input
                  type="text"
                  value={catalogForm.tagline}
                  onChange={(e) => setCatalogForm({ ...catalogForm, tagline: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Location:</label>
                <input
                  type="text"
                  value={catalogForm.location}
                  onChange={(e) => setCatalogForm({ ...catalogForm, location: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Answer:</label>
                <input
                  type="text"
                  value={catalogForm.answer}
                  onChange={(e) => setCatalogForm({ ...catalogForm, answer: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Alternative answers (optional):</label>
                <textarea
                  rows="3"
                  placeholder="Enter alternatives one per line or comma-separated"
                  value={(catalogForm.alternativeAnswers || []).join('\n')}
                  onChange={(e) =>
                    setCatalogForm({
                      ...catalogForm,
                      alternativeAnswers: parseAlternativeAnswers(e.target.value),
                    })
                  }
                />
              </div>
              <div className="form-group">
                <label>Default Public Code:</label>
                <input
                  type="text"
                  value={catalogForm.publicCode}
                  onChange={(e) => setCatalogForm({ ...catalogForm, publicCode: e.target.value })}
                  placeholder="Optional seed code when added to events"
                />
              </div>
              <div className="form-group form-group-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={!!catalogForm.onDashboard}
                    onChange={(e) => setCatalogForm({ ...catalogForm, onDashboard: e.target.checked })}
                  />
                  {' '}Include on main dashboard
                </label>
              </div>
              {catalogForm.onDashboard && (
                <div className="form-group">
                  <label>Dashboard / interact URL:</label>
                  <input
                    type="url"
                    value={catalogForm.dashboardUrl || ''}
                    onChange={(e) => setCatalogForm({ ...catalogForm, dashboardUrl: e.target.value })}
                  />
                </div>
              )}
              {catalogForm.type === 'quiz' && (
                <QuizQuestionsEditor
                  questions={catalogForm.questions || []}
                  onChange={(questions) => setCatalogForm({ ...catalogForm, questions })}
                />
              )}
              {catalogForm.type === 'jumpItOrFixIt' && (
                <div className="form-group">
                  <label>Jump It or Fix It — stations</label>
                  <JumpItOrFixItStationsAdminEditor
                    stations={catalogForm.jumpItStations || []}
                    onChange={(jumpItStations) => setCatalogForm({ ...catalogForm, jumpItStations })}
                    customLocations={catalogForm.jumpItCustomSponsorLocations || []}
                    onCustomLocationsChange={(jumpItCustomSponsorLocations) =>
                      setCatalogForm({ ...catalogForm, jumpItCustomSponsorLocations })
                    }
                    mergedSponsors={jumpItMergedSponsors}
                    sponsorsLoading={jumpItSponsorsLoading}
                    publicCode={catalogForm.publicCode}
                    eventId={selectedEvent}
                    printEventTitle={activeEvents.find((e) => e.id === selectedEvent)?.title || ''}
                    printPuzzleTitle={
                      (catalogForm.title || '').trim() ||
                      (catalogForm.number !== '' && catalogForm.number != null
                        ? `Bonus puzzle ${catalogForm.number}`
                        : '')
                    }
                    printFooterLeft={
                      (catalogForm.location || '').trim() ||
                      activeEvents.find((e) => e.id === selectedEvent)?.title ||
                      ''
                    }
                  />
                </div>
              )}
              </div>
              <div className="fm-modal-footer">
                <button
                  type="button"
                  className="fm-modal-btn fm-modal-btn-cancel"
                  onClick={() => {
                    setCatalogModalOpen(false);
                    setEditingCatalogId(null);
                    resetCatalogForm();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="fm-modal-btn fm-modal-btn-primary">
                  {editingCatalogId ? 'Update catalog puzzle' : 'Add catalog puzzle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal for duplicating a bonus puzzle */}
      {duplicateModalOpen && (
        <div className="fm-modal-overlay" role="presentation" onClick={handleCancelDuplicate}>
          <div
            className="fm-modal fm-modal--sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bp-dup-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="bp-dup-modal-title">
                Duplicate puzzle
              </h2>
              <button type="button" className="fm-modal-close" onClick={handleCancelDuplicate} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="fm-modal-body">
            <div className="form-group">
              <label>Select Event to Duplicate To:</label>
              <select
                value={duplicateEvent}
                onChange={(e) => setDuplicateEvent(e.target.value)}
              >
                {activeEvents.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.title}
                  </option>
                ))}
              </select>
            </div>
            </div>
            <div className="fm-modal-footer">
              <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={handleCancelDuplicate}>
                Cancel
              </button>
              <button type="button" className="fm-modal-btn fm-modal-btn-primary" onClick={handleConfirmDuplicate}>
                Confirm duplicate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for print sheet format */}
      {printSheetPuzzle && (
        <div className="fm-modal-overlay" role="presentation" onClick={() => setPrintSheetPuzzle(null)}>
          <div
            className="fm-modal fm-modal--md"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bp-print-sheet-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="bp-print-sheet-title">
                Print sheet — bonus clue #{printSheetPuzzle.number}
              </h2>
              <button type="button" className="fm-modal-close" onClick={() => setPrintSheetPuzzle(null)} aria-label="Close">
                ✕
              </button>
            </div>
            <div className="fm-modal-body">
              <p className="print-sheet-format-prompt">Choose format:</p>
            </div>
            <div className="fm-modal-footer print-sheet-format-footer">
              <div className="fm-modal-footer-start print-sheet-options">
                <button
                  type="button"
                  className="fm-modal-btn fm-modal-btn-primary"
                  onClick={() => handleGeneratePrintSheet('landscape')}
                >
                  Single sheet (landscape, 8.5×11)
                </button>
                <button type="button" className="fm-modal-btn fm-modal-btn-primary" onClick={() => handleGeneratePrintSheet('full')}>
                  Full sheet
                </button>
                <button type="button" className="fm-modal-btn fm-modal-btn-primary" onClick={() => handleGeneratePrintSheet('half')}>
                  Half sheet
                </button>
                <button type="button" className="fm-modal-btn fm-modal-btn-primary" onClick={() => handleGeneratePrintSheet('business')}>
                  Sheet of business cards
                </button>
              </div>
              <button type="button" className="fm-modal-btn fm-modal-btn-cancel" onClick={() => setPrintSheetPuzzle(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {puzzleTableExport && (
        <AdminColumnExportModal
          mode={puzzleTableExport.mode}
          columns={PUZZLE_TABLE_EXPORT_COLS}
          selectedIds={puzzleExportCols}
          onChangeSelected={setPuzzleExportCols}
          onCancel={() => setPuzzleTableExport(null)}
          onConfirm={runPuzzleTableExport}
          onResetDefaults={resetPuzzleExportCols}
        />
      )}
    </div>
  );
};

export default BonusPuzzleManagement;
